import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";

import {
  TFunction,
  useAddCropMixComposerCrops,
  useAddFieldCropPractice,
  useCalculateCoverCropMixes,
  useCancelAsyncOnUnmount,
  useDeleteCropMixComposerSettings,
  useGeneralContext,
  useGetCropMixComposerCrops,
  useGetCropMixComposerScoreFactors,
  useGetCropMixComposerSettings,
  useGetFieldPractices,
  useGetPracticeCrops,
  useLocation,
  useMutateCropMixComposerSettings,
  useNavigate,
  useParams,
  useTranslation,
  useUserContext,
  useValidateCropPeriods,
} from "utils";

import { CALCULATE_COVER_CROP_MIXES, GET_FIELDS_CROP_RECOMMENDATIONS } from "consts";

import {
  CMC_MIX_TYPE,
  CROP_MIX_COMPOSER_GOALS,
  CROP_ROTATION,
  CROP_TYPE_FCPO,
  CropFCPO,
  CropItemData,
  CropMixComposerCrop,
  CropMixComposerGoalResponse,
  CropMixComposerSettings,
  MainCropSettingsCMC,
  MixDistributionShare,
  MutateCropMixComposerPreferencesRequest,
  RecommendationSettings,
} from "models";

import { Drawer, ExtraInfo, Flex, Icon, MainPage, Message, PageSpin, Trans } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider.tsx";
import { CropMixComposerFooter } from "pages/app/crop-mix-composer/components/CropMixComposerFooter";
import { CropMixComposerHeader } from "pages/app/crop-mix-composer/components/CropMixComposerHeader.tsx";
import { CropMixComposerInfoSection } from "pages/app/crop-mix-composer/components/CropMixComposerInfoSection.tsx";
import { CropMixSelectionModal } from "pages/app/crop-mix-composer/components/CropMixSelectionModal.tsx";
import { GoalsSelection } from "pages/app/crop-mix-composer/components/GoalsSelection";
import { MixComposition } from "pages/app/crop-mix-composer/components/MixComposition";
import { MixOverview } from "pages/app/crop-mix-composer/components/MixOverview";
import { CROP_FAMILIES_TO_EXCLUDE_OPTIONS, SequenceAndConditions } from "pages/app/crop-mix-composer/components/SequenceAndConditions";
import { CommercialCropMixesContent } from "pages/app/crop-mix-composer/components/commercialMix/CommercialCropMixes.tsx";
import {
  addMainCropToRotation,
  calculateMixDistributionShares,
  calculateMixDistributionSharesForAutoGeneratedMix,
  getNextAvailableMixName,
  validateOnboarding1,
} from "pages/app/crop-mix-composer/utils/cropMixComposer";
import { TimelineCalendarItemsProps } from "pages/app/field-timeline/components/FieldDetailsTimeline";
import { getCropsBeforeAndAfter, getNextCrop } from "pages/app/field-timeline/utils/TimelineUtils";

const MIX_TYPE_ID = {
  WINTER_SIMPLE: "7664dd56-2b55-4b40-a7f3-5c2e1f19f6da", // catch_crop_green_cover
  WINTER_COMPLEX: "53076fca-35e1-4fd0-ad92-808b54c0dac5", // winter_catch_crop_mixtures
  SUMMER_SIMPLE: "bb0c0d54-9936-4912-9495-e1100749e10b", // catch_crop_as_summer_crop_stubble_sowing
  SUMMER_COMPLEX: "cdbeaa49-2ca1-4122-9beb-be011a4749be", // summer_catch_crop_mixtures
};

export enum CMC_ROUTES {
  COMPOSITION = "composition",
  GOALS = "goals",
  OVERVIEW = "overview",
  SETTINGS = "settings",
  COM_MIXES = "com-mixes",
}

const ONBOARDING_STEPS_ORDER = [CMC_ROUTES.SETTINGS, CMC_ROUTES.GOALS, CMC_ROUTES.COMPOSITION, CMC_ROUTES.OVERVIEW];

export const MAX_SELECTABLE_GOALS = 3;

export enum CMC_ONBOARDING_1_ERRORS {
  SOIL_TYPE = "soilType",
  SOIL_PH = "soilPh",
  DROUGHT = "drought",
  MAIN_CROP_1 = "mainCrop1",
  MAIN_CROP_2 = "mainCrop2",
  SEEDING_BEFORE_HARVEST = "seedingBeforeHarvest",
  MINIMUM_GROWING_PERIOD = "minimumGrowingPeriod",
  LEGUME_EXCLUSION_CONFIRMATION = "legumeExclusionConfirmation",
}

const Settings = ({
  setShowInfoDrawer,
  t,
  fieldId,
  selectedRecommendationItemOnTimeline,
}: {
  setShowInfoDrawer: (show: boolean) => void;
  t: TFunction;
  fieldId?: string;
  selectedRecommendationItemOnTimeline?: TimelineCalendarItemsProps<CropItemData>;
}) => (
  <>
    <CropMixComposerInfoSection
      className="info"
      headline={t(`page.${CMC_ROUTES.SETTINGS}.info.headline`)}
      content={t(`page.${CMC_ROUTES.SETTINGS}.info.content`)}
      info={t(`page.${CMC_ROUTES.SETTINGS}.info.info`)}
      onInfoClicked={setShowInfoDrawer}
      dataTestId="sequence-and-conditions"
    />
    <div className="content" data-testid={"crop-mix-composer-overlay-step-sequence-and-conditions"}>
      <SequenceAndConditions fieldId={fieldId} selectedRecommendationItemOnTimeline={selectedRecommendationItemOnTimeline} />
    </div>
  </>
);

const Goals = ({ setShowInfoDrawer, maxSelectableItems, t }: { setShowInfoDrawer: (show: boolean) => void; maxSelectableItems: number; t: TFunction }) => {
  const { autoMixMode, isChangingStep, activeCropMixId } = useCropMixComposerContext();
  const { data: settingsCMC } = useGetCropMixComposerSettings();

  const activeSettingsCMC = settingsCMC?.find((setting) => setting.id === activeCropMixId);
  const mixType = activeSettingsCMC?.mixType || null;

  return autoMixMode && isChangingStep !== null && !mixType ? (
    <AutoMixLoadingScreen t={t} />
  ) : (
    <>
      <CropMixComposerInfoSection
        className="info"
        headline={t(`page.${CMC_ROUTES.GOALS}.info.headline`)}
        content={<Trans i18nKey={`cropMixComposer:page.${CMC_ROUTES.GOALS}.info.content`} components={{ strong: <strong /> }} />}
        info={t(`page.${CMC_ROUTES.GOALS}.info.info`)}
        onInfoClicked={setShowInfoDrawer}
        dataTestId="goals"
      />
      <div className="content">
        <GoalsSelection items={CROP_MIX_COMPOSER_GOALS} maxSelectableItems={maxSelectableItems} t={t} />
      </div>
    </>
  );
};

const AutoMixLoadingScreen = ({ t }: { t: TFunction }) => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);

  const loadingMessageKeys = ["searchingCoverCrops", "findingNutrientBindingCrops", "selectingBestMatch", "searchingSoilTypeMatch"];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => (prevIndex + 1) % loadingMessageKeys.length);
    }, 1500);

    return () => clearInterval(interval);
  }, [loadingMessageKeys.length]);

  return (
    <Flex vertical align="center" justify="center" gap="0.75rem" data-testid="auto-mix-loading-screen" className="width-100">
      <Icon iconName="klim/plant-ai" size="xl-light" strokeWidth={0.75} />
      <p>{t(`page.${CMC_ROUTES.GOALS}.autoMixModeLoading.${loadingMessageKeys[currentMessageIndex]}`)}</p>
    </Flex>
  );
};

export const CropMixComposerOverlay = ({
  visible,
  regionId,
  setShowCropMixComposer,
  openInDrawer,
  fieldId,
}: {
  visible: boolean;
  regionId?: string;
  setShowCropMixComposer?: Dispatch<SetStateAction<boolean>>;
  openInDrawer?: boolean;
  fieldId?: string;
}) => {
  const { t } = useTranslation("cropMixComposer");
  const signal = useCancelAsyncOnUnmount()();
  const queryClient = useQueryClient();

  const {
    activeCropMixId,
    autoMixMode,
    cropRotationFamiliesToExclude,
    currentStep,
    drought,
    isPageLoading,
    legumeExclusionConfirmation,
    mainCropSettings,
    selectedCrops,
    selectedGoals,
    selectedRecommendationItemOnTimeline,
    soilPh,
    soilType,
    weightShareDistribution,
    setActiveCropMixId,
    setAggregateGoalScores,
    setAggregateMatchScore,
    setCMCMixCropFCPO,
    setCropRotationFamiliesToExclude,
    setCurrentStep,
    setDrought,
    setIsChangingStep,
    setIsNewCropMix,
    setIsPageLoading,
    setLegumeExclusionConfirmation,
    setMainCropSettings,
    setOnboarding1Errors,
    setPotentialPayout,
    setRegionId,
    setSelectedCrops,
    setSelectedGoals,
    setShowDrawerFromCMC,
    setSoilPh,
    setSoilType,
    setWeightShareDistribution,
    setShowAutoMixFailureMessage,
  } = useCropMixComposerContext();
  const { setNavigationCrumbs } = useGeneralContext();
  const { user } = useUserContext();

  const [showInfoDrawer, setShowInfoDrawer] = useState<boolean>(false);
  const [hasSettingsChanged, setHasSettingsChanged] = useState<boolean>(false);

  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const isCmcPath = location.pathname.startsWith("/cmc");
  const isVisible = visible || isCmcPath;
  const basePath = `${location.pathname.split("cmc")[0].replace(/\/$/, "")}/`;
  const showCropMixSelectionModal = !fieldId && isVisible && isCmcPath && !params.cropMixId && !activeCropMixId;

  const { data: cropMixComposerResponse, isFetching: isFetchingCropMixComposerResponse } = useCalculateCoverCropMixes({ cropMixId: params.cropMixId });
  const { data: settingsCMC, isFetching: isFetchingSettingsCMC } = useGetCropMixComposerSettings();

  const { mutateAsync: mutateAsyncCropMixComposerSettings } = useMutateCropMixComposerSettings();
  const { data: cropsCMC, isPending: isPendingCropsCMC } = useGetCropMixComposerCrops(params.cropMixId);
  const { mutateAsync: mutateAsyncAddCropMixComposerCrops } = useAddCropMixComposerCrops();
  const { isPending: isPendingScoreFactors } = useGetCropMixComposerScoreFactors();
  const { data: crops } = useGetPracticeCrops({ regionId, enabled: true });
  const { data: practicesResponse, isLoading: isLoadingPracticesResponse } = useGetFieldPractices({ fieldId: fieldId || "", inView: !!fieldId });
  const { mutateAsync: addFieldCropPractice } = useAddFieldCropPractice({ fieldId: params?.fieldId || "", farmId: user?.farm?.id || undefined });
  const { mutateAsync: deleteCropMixComposerSettings } = useDeleteCropMixComposerSettings();
  const { validatePeriods } = useValidateCropPeriods();

  const resetSettings = useCallback(() => {
    setSoilType(undefined);
    setSoilPh(undefined);
    setDrought(undefined);
    setCropRotationFamiliesToExclude([]);
    setLegumeExclusionConfirmation(null);
    setMainCropSettings(undefined);
    setSelectedGoals([]);
    setSelectedCrops([]);
    setAggregateGoalScores([]);
    setAggregateMatchScore(0);
    setWeightShareDistribution({});
    setActiveCropMixId(undefined);
    setHasSettingsChanged(false);
    setCurrentStep(null);
    setOnboarding1Errors([]);
    setPotentialPayout(0);
    setShowAutoMixFailureMessage(false);
  }, [
    setSoilType,
    setSoilPh,
    setDrought,
    setCropRotationFamiliesToExclude,
    setLegumeExclusionConfirmation,
    setMainCropSettings,
    setSelectedGoals,
    setSelectedCrops,
    setAggregateGoalScores,
    setAggregateMatchScore,
    setWeightShareDistribution,
    setActiveCropMixId,
    setCurrentStep,
    setOnboarding1Errors,
    setPotentialPayout,
    setShowAutoMixFailureMessage,
  ]);

  const isValidStepFromPath = useCallback(() => {
    const isValidStep = !!params.step && Object.values(CMC_ROUTES).includes(params.step as CMC_ROUTES);
    return isValidStep;
  }, [params.step]);

  const updateLocalSettings = useCallback(
    (cmcSettings: CropMixComposerSettings, cropsCMC: CropMixComposerCrop[], cropMixId: string) => {
      if (!cmcSettings) return;

      const { goals, recommendation } = cmcSettings;
      const { settings, mainCropSettings } = recommendation;

      setSelectedGoals(goals);
      setSoilType(settings.soilType || undefined);
      setSoilPh(settings.soilPh || undefined);
      setDrought(typeof settings.isDrought === "boolean" ? settings.isDrought : undefined);

      const savedCrop1Family = mainCropSettings.crop1Id ? crops?.find((crop) => crop.id === mainCropSettings.crop1Id)?.cropFamily : null;
      const savedCrop2Family = mainCropSettings.crop2Id ? crops?.find((crop) => crop.id === mainCropSettings.crop2Id)?.cropFamily : null;

      // if we one or more main crops are missing, step 1 isn't saved, so the legume confirmation should be "null"
      // if we have legumes saved in the DB, the confirmation selections should be "false"
      // if one of the main crops is a legume, but legume isn't saved in the DB, the confirmation should be "true"
      // in any other case, we keep the confirmation as unset -> "null"
      const updatedLegumeExclusionConfirmation =
        !mainCropSettings.crop1Id || !mainCropSettings.crop2Id
          ? null
          : settings.cropRotationFamiliesToExclude.includes(CROP_ROTATION.FABACEAE)
          ? false
          : savedCrop1Family === CROP_ROTATION.FABACEAE || savedCrop2Family === CROP_ROTATION.FABACEAE
          ? true
          : null;

      // for the interface to make sense, we need to inject the legume family in the excluded list if the "updatedLegumeExclusionConfirmation" is "true",
      // otherwise this will break the logic if a user changes the legume exclusion confirmation preference without changing any other value
      const updatedCropRotationFamiliesToExclude =
        updatedLegumeExclusionConfirmation === true
          ? [...settings.cropRotationFamiliesToExclude, CROP_ROTATION.FABACEAE]
          : settings.cropRotationFamiliesToExclude;

      setLegumeExclusionConfirmation(updatedLegumeExclusionConfirmation);
      setCropRotationFamiliesToExclude(updatedCropRotationFamiliesToExclude);

      const updatedMainCropSettings: MainCropSettingsCMC = {
        crop1Id: mainCropSettings.crop1Id || undefined,
        crop2Id: mainCropSettings.crop2Id || undefined,
        seedingDate: mainCropSettings.seedingDate || undefined,
        harvestDate: mainCropSettings.harvestDate || undefined,
      };

      setMainCropSettings(updatedMainCropSettings);

      let updatedSelectedCrops: CropMixComposerGoalResponse[] = [];
      let updatedWeightShareDistribution: MixDistributionShare = {};

      if (cropMixComposerResponse) {
        updatedSelectedCrops =
          !cropsCMC || cropsCMC.length === 0
            ? []
            : cropsCMC
                .map((crop) => cropMixComposerResponse.find((c) => c.crop.id === crop.cropId))
                .filter((crop): crop is CropMixComposerGoalResponse => crop !== undefined);
        updatedWeightShareDistribution = !cropsCMC || cropsCMC.length === 0 ? {} : calculateMixDistributionShares(updatedSelectedCrops, cropsCMC);

        setSelectedCrops(updatedSelectedCrops);
        setWeightShareDistribution(updatedWeightShareDistribution);
      }

      const { isValid: settingsCompleted } = validateOnboarding1(
        {
          crop1Id: mainCropSettings.crop1Id || undefined,
          crop2Id: mainCropSettings.crop2Id || undefined,
          seedingDate: mainCropSettings.seedingDate || undefined,
          harvestDate: mainCropSettings.harvestDate || undefined,
        },
        settings.soilType,
        settings.soilPh,
        settings.isDrought,
        settings.cropRotationFamiliesToExclude,
        legumeExclusionConfirmation
      );
      const goalsCompleted = settingsCompleted && goals.length === MAX_SELECTABLE_GOALS;
      const compositionCompleted =
        settingsCompleted &&
        goalsCompleted &&
        updatedSelectedCrops.length > 0 &&
        Object.keys(updatedWeightShareDistribution).length === updatedSelectedCrops.length;

      const updatedStep =
        currentStep === CMC_ROUTES.COM_MIXES && compositionCompleted
          ? CMC_ROUTES.COM_MIXES
          : compositionCompleted
          ? CMC_ROUTES.OVERVIEW
          : goalsCompleted
          ? CMC_ROUTES.COMPOSITION
          : settingsCompleted
          ? CMC_ROUTES.GOALS
          : CMC_ROUTES.SETTINGS;
      setActiveCropMixId(cropMixId);
      if (settingsCompleted && goalsCompleted) queryClient.invalidateQueries({ queryKey: [CALCULATE_COVER_CROP_MIXES, cropMixId] });

      const isExistingStep = isValidStepFromPath();
      const isValidStep =
        currentStep === updatedStep ||
        (currentStep === CMC_ROUTES.COM_MIXES && compositionCompleted) ||
        (currentStep === CMC_ROUTES.OVERVIEW && compositionCompleted) ||
        (currentStep === CMC_ROUTES.COMPOSITION && goalsCompleted) ||
        (currentStep === CMC_ROUTES.GOALS && settingsCompleted) ||
        currentStep === CMC_ROUTES.SETTINGS;

      if (!isExistingStep || !isValidStep) {
        setCurrentStep(updatedStep);
      }
    },
    [
      cropMixComposerResponse,
      crops,
      currentStep,
      isValidStepFromPath,
      legumeExclusionConfirmation,
      queryClient,
      setActiveCropMixId,
      setCropRotationFamiliesToExclude,
      setCurrentStep,
      setDrought,
      setLegumeExclusionConfirmation,
      setMainCropSettings,
      setSelectedCrops,
      setSelectedGoals,
      setSoilPh,
      setSoilType,
      setWeightShareDistribution,
    ]
  );

  const hasLocalSettingsChanged = useCallback(
    (savedPreferences: CropMixComposerSettings): boolean => {
      if (!savedPreferences?.recommendation?.settings || !savedPreferences?.recommendation?.mainCropSettings) return false;

      const hasSoilSettingsChanged =
        soilType !== savedPreferences.recommendation.settings.soilType ||
        soilPh !== savedPreferences.recommendation.settings.soilPh ||
        drought !== savedPreferences.recommendation.settings.isDrought;

      const updatedCropRotationFamiliesToExclude =
        cropRotationFamiliesToExclude.includes(CROP_ROTATION.FABACEAE) && legumeExclusionConfirmation === true
          ? cropRotationFamiliesToExclude.filter((family) => family !== CROP_ROTATION.FABACEAE)
          : cropRotationFamiliesToExclude;

      const hasCropRotationFamiliesToExcludeChanged =
        updatedCropRotationFamiliesToExclude.length !== savedPreferences.recommendation.settings.cropRotationFamiliesToExclude.length ||
        updatedCropRotationFamiliesToExclude.some((family) => !savedPreferences.recommendation.settings.cropRotationFamiliesToExclude.includes(family));

      const hasMainCropSettingsChanged =
        mainCropSettings?.crop1Id !== savedPreferences.recommendation.mainCropSettings.crop1Id ||
        mainCropSettings?.crop2Id !== savedPreferences.recommendation.mainCropSettings.crop2Id ||
        mainCropSettings?.seedingDate !== savedPreferences.recommendation.mainCropSettings.seedingDate ||
        mainCropSettings?.harvestDate !== savedPreferences.recommendation.mainCropSettings.harvestDate;

      const hasGoalsChanged = selectedGoals.length !== savedPreferences.goals.length || !selectedGoals.every((goal) => savedPreferences.goals.includes(goal));

      return hasSoilSettingsChanged || hasCropRotationFamiliesToExcludeChanged || hasMainCropSettingsChanged || hasGoalsChanged;
    },
    [cropRotationFamiliesToExclude, drought, legumeExclusionConfirmation, mainCropSettings, selectedGoals, soilPh, soilType]
  );

  const updatePreferences = useCallback(async () => {
    if (!activeCropMixId) return;

    const savedPreferences = settingsCMC?.find((mix) => mix.id === activeCropMixId);
    if (!savedPreferences) return;

    if (!hasLocalSettingsChanged(savedPreferences)) return;

    const requestBody: MutateCropMixComposerPreferencesRequest = {
      name: savedPreferences.name,
      goals: selectedGoals,
      recommendation: {
        settings: {
          soilType,
          soilPh,
          isDrought: drought,
          cropRotationFamiliesToExclude:
            cropRotationFamiliesToExclude.includes(CROP_ROTATION.FABACEAE) && legumeExclusionConfirmation === true
              ? cropRotationFamiliesToExclude.filter((family) => family !== CROP_ROTATION.FABACEAE)
              : cropRotationFamiliesToExclude,
        },
        mainCropSettings,
      },
    };

    await mutateAsyncCropMixComposerSettings({ data: requestBody, settingId: activeCropMixId });
  }, [
    activeCropMixId,
    settingsCMC,
    hasLocalSettingsChanged,
    selectedGoals,
    soilType,
    soilPh,
    drought,
    cropRotationFamiliesToExclude,
    legumeExclusionConfirmation,
    mainCropSettings,
    mutateAsyncCropMixComposerSettings,
  ]);

  const hasLocalCropsCompositionChanged = useCallback((newMixDistributionShare: MixDistributionShare, savedComposition: CropMixComposerCrop[] | undefined) => {
    const crops = Object.entries(newMixDistributionShare);
    const newComposition = crops.map(([cropId, mixDistributionShare]) => ({
      cropId,
      weightShare: mixDistributionShare.weightShare,
    }));

    if (newComposition.length === 0 && (!savedComposition || savedComposition.length === 0)) {
      return false;
    }

    if (newComposition.length !== (savedComposition?.length || 0)) {
      return true;
    }

    return newComposition.some((crop) => {
      const savedCrop = savedComposition?.find((c) => c.cropId === crop.cropId);
      if (!savedCrop) return true;

      return savedCrop.weightShare !== crop.weightShare;
    });
  }, []);

  const updateSelectedCrops = useCallback(
    async (activeCropMixId: string | undefined, weightShareDistribution: MixDistributionShare, cropsCMC: CropMixComposerCrop[] | undefined) => {
      if (!activeCropMixId) return;

      // Check if the composition has changed
      const hasCompositionChanged = hasLocalCropsCompositionChanged(weightShareDistribution, cropsCMC);

      if (!hasCompositionChanged) return;

      const crops = Object.entries(weightShareDistribution);
      if (!crops || crops.length === 0) return;

      const composition = crops.map(([cropId, mixDistributionShare]) => ({
        cropId,
        weightShare: mixDistributionShare.weightShare,
      }));

      await mutateAsyncAddCropMixComposerCrops({ cropMixId: activeCropMixId, data: { composition } });
    },
    [hasLocalCropsCompositionChanged, mutateAsyncAddCropMixComposerCrops]
  );

  const handleClose = () => {
    if (params?.cropMixId && (!selectedRecommendationItemOnTimeline?.data.cropMixId || !settingsCMC?.some((mix) => mix.id === params.cropMixId))) {
      deleteCropMixComposerSettings({ cropMixId: params?.cropMixId ?? "" });
    }

    setShowCropMixComposer?.(false);
    resetSettings();
    navigate(basePath);
  };

  const findNextStep = (currentStep: CMC_ROUTES | null, direction: "next" | "previous") => {
    if (!currentStep) return CMC_ROUTES.SETTINGS;

    const currentStepIndex = ONBOARDING_STEPS_ORDER.indexOf(currentStep);
    const nextStepIndex = direction === "next" ? currentStepIndex + 1 : currentStepIndex - 1;

    return ONBOARDING_STEPS_ORDER[nextStepIndex] || currentStep;
  };

  const getUpdatedMixType = (currentMixType: CMC_MIX_TYPE | null, hasCompositionChanged: boolean) => {
    if (currentMixType && !hasCompositionChanged) return currentMixType;
    if (!currentMixType) return CMC_MIX_TYPE.MANUAL;
    if (currentMixType === CMC_MIX_TYPE.AUTO_GENERATED && hasCompositionChanged) return CMC_MIX_TYPE.AUTO_GENERATED_EDITED;
    return currentMixType;
  };

  const handleNextButton = async (mixType: CMC_MIX_TYPE | null) => {
    setIsChangingStep("next");

    const { isValid: settingsCompleted, errors: onboarding1ValidationErrors } = validateOnboarding1(
      mainCropSettings,
      soilType,
      soilPh,
      drought,
      cropRotationFamiliesToExclude,
      legumeExclusionConfirmation
    );
    const goalsCompleted = selectedGoals.length === MAX_SELECTABLE_GOALS;
    const compositionCompleted = selectedCrops.length > 0 && Object.keys(weightShareDistribution).length === selectedCrops.length;

    setOnboarding1Errors(onboarding1ValidationErrors);

    try {
      if (currentStep === CMC_ROUTES.SETTINGS && !settingsCompleted) {
        Message({ content: t(`errors.${CMC_ROUTES.SETTINGS}`), icon: "error" });
        return;
      }

      if (currentStep === CMC_ROUTES.GOALS && !goalsCompleted) {
        Message({ content: t(`errors.${CMC_ROUTES.GOALS}`, { goalCount: MAX_SELECTABLE_GOALS }), icon: "error" });
        return;
      }

      if (currentStep === CMC_ROUTES.COMPOSITION && !compositionCompleted) {
        Message({ content: t(`errors.${CMC_ROUTES.COMPOSITION}`), icon: "error" });
        return;
      }

      const activeCropMixSettings = settingsCMC?.find((mix) => mix.id === activeCropMixId) as CropMixComposerSettings;
      const shouldUpdateSettings = hasLocalSettingsChanged(activeCropMixSettings);
      const hasCompositionChanged = hasLocalCropsCompositionChanged(weightShareDistribution, cropsCMC);

      // since we are saving the settings at every step, we want to invalidate the query only once if either step 1 or 2 have changes
      if (!hasSettingsChanged) setHasSettingsChanged(shouldUpdateSettings);

      const shouldInvalidateCropMixComposerResponseData = shouldUpdateSettings || hasSettingsChanged;

      await Promise.all([updatePreferences(), updateSelectedCrops(activeCropMixId, weightShareDistribution, cropsCMC)]);

      // if we are in auto mix mode, we want to fetch the data from the microservice and update the selected crops
      // if we have the mix type, it means we are in edit mode, so we don't want to change the mix composition, just recalculate the match score and the goals
      if (currentStep === CMC_ROUTES.GOALS && autoMixMode && !mixType) {
        // Add a minimum wait time to show the loading screen
        const updatedCropMixComposerResponse: CropMixComposerGoalResponse[] = await Promise.all([
          queryClient.fetchQuery({
            queryKey: [CALCULATE_COVER_CROP_MIXES, activeCropMixId],
          }) as Promise<CropMixComposerGoalResponse[]>,
          new Promise<void>((resolve) => {
            setTimeout(resolve, 4500);
          }),
        ]).then(([response]) => response);

        const updatedSelectedCrops = updatedCropMixComposerResponse.filter((crop) => crop.autoMixWeightShare > 0);
        const updatedWeightShareDistribution = calculateMixDistributionSharesForAutoGeneratedMix(updatedSelectedCrops);

        setSelectedCrops(updatedSelectedCrops);
        setWeightShareDistribution(updatedWeightShareDistribution);
        await updateSelectedCrops(activeCropMixId, updatedWeightShareDistribution, cropsCMC);

        setHasSettingsChanged(false);
        setShowAutoMixFailureMessage(false);

        // Check if smart mix generation failed (all weightShare values are 0)
        const allWeightSharesZero = Object.values(updatedWeightShareDistribution).every((share) => share.weightShare === 0);
        if (allWeightSharesZero) {
          setShowAutoMixFailureMessage(true);
        }

        if (updatedSelectedCrops.length > 0 && Object.keys(updatedWeightShareDistribution).length === updatedSelectedCrops.length) {
          await mutateAsyncCropMixComposerSettings({
            data: { mixType: CMC_MIX_TYPE.AUTO_GENERATED, name: activeCropMixSettings.name },
            settingId: activeCropMixId,
          });
          navigate(`${basePath}cmc/${activeCropMixId}/${CMC_ROUTES.OVERVIEW}${location.search}`);
          return;
        }
      }

      // if we are on the goals page and we don't have the mix type, it means we are in the onboarding flow, in this case we must fetch the crops data from the microservice
      // if we are in any step and we have a mix type, it means we are in edit mode, so we should fetch the crops data from the microservice if the settings have changed and recalculate the mix
      if ((currentStep === CMC_ROUTES.GOALS && !mixType) || (mixType && shouldInvalidateCropMixComposerResponseData)) {
        await queryClient.refetchQueries({
          queryKey: [CALCULATE_COVER_CROP_MIXES, activeCropMixId],
        });
        const updatedCropMixComposerResponse: CropMixComposerGoalResponse[] = queryClient.getQueryData([CALCULATE_COVER_CROP_MIXES, activeCropMixId]) || [];
        const updatedSelectedCrops = selectedCrops
          .map((crop) => updatedCropMixComposerResponse.find((c) => c.crop.id === crop.crop.id))
          .filter((crop): crop is CropMixComposerGoalResponse => crop !== undefined);
        const updatedWeightShareDistribution = calculateMixDistributionShares(updatedSelectedCrops, cropsCMC);

        setSelectedCrops(updatedSelectedCrops);
        setWeightShareDistribution(updatedWeightShareDistribution);
        await updateSelectedCrops(activeCropMixId, updatedWeightShareDistribution, cropsCMC);

        setHasSettingsChanged(false);
      }

      if (currentStep === CMC_ROUTES.COMPOSITION) {
        const newMixType = getUpdatedMixType(mixType, hasCompositionChanged);
        await mutateAsyncCropMixComposerSettings({ data: { mixType: newMixType, name: activeCropMixSettings.name }, settingId: activeCropMixId });
      }

      const nextStep = mixType ? CMC_ROUTES.OVERVIEW : findNextStep(currentStep, "next");
      navigate(`${basePath}cmc/${activeCropMixId}/${nextStep}${location.search}`);
    } finally {
      setIsChangingStep(null);
    }
  };

  const handleBackButton = async () => {
    setIsChangingStep("previous");

    try {
      const activeCropMixSettings = settingsCMC?.find((mix) => mix.id === activeCropMixId) as CropMixComposerSettings;
      const shouldUpdateSettings = hasLocalSettingsChanged(activeCropMixSettings);

      // since we are saving the settings at every step, we need to keep track of the changes in a separate state
      if (!hasSettingsChanged) setHasSettingsChanged(shouldUpdateSettings);

      await Promise.all([updatePreferences(), updateSelectedCrops(activeCropMixId, weightShareDistribution, cropsCMC)]);

      const previousStep = findNextStep(currentStep, "previous");
      navigate(`${basePath}cmc/${activeCropMixId}/${previousStep}${location.search}`);
    } finally {
      setIsChangingStep(null);
    }
  };

  const handleUpdateSettings = useCallback(async () => {
    const mixToUpdateName = getNextAvailableMixName(t("cropMixSelectionModal.myMix"), settingsCMC);
    const settingsRequestBody: MutateCropMixComposerPreferencesRequest = {
      name: mixToUpdateName,
      goals: selectedGoals,
      recommendation: {
        settings: {
          soilType,
          soilPh,
          isDrought: drought,
          cropRotationFamiliesToExclude:
            cropRotationFamiliesToExclude.includes(CROP_ROTATION.FABACEAE) && legumeExclusionConfirmation === true
              ? cropRotationFamiliesToExclude.filter((family) => family !== CROP_ROTATION.FABACEAE)
              : cropRotationFamiliesToExclude,
        },
        mainCropSettings,
      },
    };

    await mutateAsyncCropMixComposerSettings({ data: settingsRequestBody, settingId: activeCropMixId });
  }, [
    t,
    settingsCMC,
    mainCropSettings,
    mutateAsyncCropMixComposerSettings,
    selectedGoals,
    soilPh,
    soilType,
    drought,
    cropRotationFamiliesToExclude,
    legumeExclusionConfirmation,
    activeCropMixId,
  ]);

  const getMixTypeId = useCallback(
    (seedingYear: number, harvestYear: number, harvestMonth: number) => {
      const isSummerCrop = harvestYear === seedingYear && harvestMonth >= 5 && harvestMonth <= 10;

      return selectedCrops.length > 1
        ? isSummerCrop
          ? MIX_TYPE_ID.SUMMER_COMPLEX
          : MIX_TYPE_ID.WINTER_COMPLEX
        : isSummerCrop
        ? MIX_TYPE_ID.SUMMER_SIMPLE
        : MIX_TYPE_ID.WINTER_SIMPLE;
    },
    [selectedCrops]
  );

  const handleApplyToField = useCallback(async () => {
    const mainCropsBeforeAndAfterRecommendation = getCropsBeforeAndAfter(practicesResponse, selectedRecommendationItemOnTimeline);
    const nextFutureCrop = getNextCrop(practicesResponse, selectedRecommendationItemOnTimeline);

    await handleUpdateSettings();

    if (!mainCropsBeforeAndAfterRecommendation.after?.cropId) {
      await addMainCropToRotation(
        t,
        mainCropsBeforeAndAfterRecommendation,
        nextFutureCrop,
        mainCropSettings,
        crops || [],
        practicesResponse,
        fieldId || "",
        signal,
        validatePeriods,
        addFieldCropPractice
      );
    }

    queryClient.invalidateQueries({ queryKey: [GET_FIELDS_CROP_RECOMMENDATIONS, [fieldId]] });

    const seedingYear = moment(mainCropSettings?.harvestDate).year();
    const harvestYear = moment(mainCropSettings?.seedingDate).year();
    const harvestMonth = moment(mainCropSettings?.seedingDate).month() + 1;

    const cmcMixCropFCPO: CropFCPO = {
      cropId: getMixTypeId(seedingYear, harvestYear, harvestMonth),
      cropType: CROP_TYPE_FCPO.COVER,
      seedingStartDate: moment(mainCropSettings?.harvestDate)
        .add(1, "day")
        .toISOString(),
      seedingEndDate: null,
      harvestStartDate: null,
      harvestEndDate: moment(mainCropSettings?.seedingDate)
        .subtract(1, "day")
        .toISOString(),
      seedingPracticeType: null,
      hasResidue: null,
      cropMixId: activeCropMixId,
      id: selectedRecommendationItemOnTimeline?.id?.toString() || "",
      cropName: selectedRecommendationItemOnTimeline?.data.cropName || "",
      isConfirmed: false,
      payout: 0,
      translationSlug: "",
      yieldTonsPerHa: null,
      year: seedingYear,
      seedingVerificationStatus: null,
    };

    setCMCMixCropFCPO(cmcMixCropFCPO);
    setShowDrawerFromCMC(true);
  }, [
    crops,
    signal,
    validatePeriods,
    addFieldCropPractice,
    t,
    activeCropMixId,
    fieldId,
    getMixTypeId,
    handleUpdateSettings,
    mainCropSettings,
    practicesResponse,
    queryClient,
    selectedRecommendationItemOnTimeline,
    setCMCMixCropFCPO,
    setShowDrawerFromCMC,
  ]);

  const handleCreateNewMixFromFieldView = useCallback(async () => {
    const newMixName = getNextAvailableMixName(t("cropMixSelectionModal.myMix"), settingsCMC);

    const mainCropsBeforeAndAfterRecommendation = getCropsBeforeAndAfter(practicesResponse, selectedRecommendationItemOnTimeline);

    const mainCropSettings: MainCropSettingsCMC = {
      crop1Id: mainCropsBeforeAndAfterRecommendation.before?.cropId,
      crop2Id: mainCropsBeforeAndAfterRecommendation.after?.cropId,
      seedingDate: mainCropsBeforeAndAfterRecommendation.after?.seedingStartDate,
      harvestDate: mainCropsBeforeAndAfterRecommendation.before?.harvestEndDate,
    };

    const selectedCropFamilies = [mainCropSettings.crop1Id, mainCropSettings.crop2Id]
      .filter((cropId) => cropId !== undefined)
      .map((cropId) => crops?.find((crop) => crop.id === cropId)?.cropFamily);
    const cropRotationFamiliesToExclude = CROP_FAMILIES_TO_EXCLUDE_OPTIONS.filter((family) => selectedCropFamilies.includes(family));

    const settings: RecommendationSettings = {
      soilType: settingsCMC?.[0]?.recommendation?.settings?.soilType || undefined,
      soilPh: settingsCMC?.[0]?.recommendation?.settings?.soilPh || undefined,
      isDrought: typeof settingsCMC?.[0]?.recommendation?.settings?.isDrought === "boolean" ? settingsCMC?.[0]?.recommendation?.settings?.isDrought : undefined,
      cropRotationFamiliesToExclude,
    };

    const newMix = await mutateAsyncCropMixComposerSettings({
      data: {
        name: newMixName,
        recommendation: { settings, mainCropSettings },
      },
    });
    setIsNewCropMix(true);

    if (newMix) navigate(`${basePath}cmc/${newMix.id}/${CMC_ROUTES.SETTINGS}${location.search}`);
  }, [
    basePath,
    crops,
    location.search,
    mutateAsyncCropMixComposerSettings,
    navigate,
    practicesResponse,
    selectedRecommendationItemOnTimeline,
    setIsNewCropMix,
    settingsCMC,
    t,
  ]);

  useEffect(() => {
    if (fieldId && visible && !params.cropMixId) {
      handleCreateNewMixFromFieldView();
    }
  }, [fieldId, handleCreateNewMixFromFieldView, params.cropMixId, visible]);

  useEffect(() => {
    if (visible) {
      setRegionId(regionId);
    }

    if (!visible && isCmcPath) {
      navigate(`${basePath}cmc${location.search}`);
    }
  }, [basePath, isCmcPath, location.search, navigate, regionId, setRegionId, visible]);

  useEffect(() => {
    if (isFetchingCropMixComposerResponse || isFetchingSettingsCMC || isPendingCropsCMC) return;

    if (!params.cropMixId) {
      resetSettings();
      return;
    }

    if (params.cropMixId === activeCropMixId) return;

    // if the crop mix id is not the same as the active crop mix id, we need to reset the settings
    resetSettings();

    const activeCropMixSettings = settingsCMC?.find((mix) => mix.id === params.cropMixId);
    // if there is no match between the crop mix id and the saved preferences, navigate back to /cmc
    // this will make the start modal appear to select an existing id or create a new mix
    if (!activeCropMixSettings) {
      resetSettings();
      navigate(`${basePath}cmc${location.search}`);
      return;
    }

    updateLocalSettings(activeCropMixSettings, cropsCMC || [], params.cropMixId);
  }, [
    activeCropMixId,
    basePath,
    cropsCMC,
    isFetchingCropMixComposerResponse,
    isPendingCropsCMC,
    isFetchingSettingsCMC,
    location.search,
    navigate,
    params.cropMixId,
    resetSettings,
    setCurrentStep,
    settingsCMC,
    updateLocalSettings,
  ]);

  useEffect(() => {
    if (!activeCropMixId || activeCropMixId !== params.cropMixId) return;

    if (params.step === undefined && currentStep) {
      navigate(`${basePath}cmc/${activeCropMixId}/${currentStep}${location.search}`);
      return;
    }

    if (isValidStepFromPath() && (params.step !== currentStep || currentStep === null)) {
      setCurrentStep(params.step as CMC_ROUTES);
      navigate(`${basePath}cmc/${activeCropMixId}/${params.step}${location.search}`);
      return;
    }

    if (!isValidStepFromPath()) {
      navigate(`${basePath}cmc/${activeCropMixId}/${location.search}`);
    }
  }, [activeCropMixId, basePath, currentStep, isValidStepFromPath, location.search, navigate, params.cropMixId, params.step, setCurrentStep, settingsCMC]);

  useEffect(() => {
    if (
      isLoadingPracticesResponse ||
      isFetchingCropMixComposerResponse ||
      isPendingCropsCMC ||
      isFetchingSettingsCMC ||
      isPendingScoreFactors ||
      ((currentStep === CMC_ROUTES.COMPOSITION || currentStep === CMC_ROUTES.OVERVIEW || currentStep === CMC_ROUTES.COM_MIXES) &&
        (!cropMixComposerResponse || cropMixComposerResponse?.length === 0))
    ) {
      setIsPageLoading(true);
    } else {
      setIsPageLoading(false);
    }
  }, [
    isLoadingPracticesResponse,
    isPendingCropsCMC,
    isPendingScoreFactors,
    isFetchingSettingsCMC,
    isFetchingCropMixComposerResponse,
    cropMixComposerResponse,
    setIsPageLoading,
    currentStep,
  ]);

  const content = () => (
    <>
      {params.cropMixId !== activeCropMixId ? (
        <PageSpin />
      ) : (
        <Flex className="body" gap="4rem" justify="center">
          {currentStep === CMC_ROUTES.SETTINGS ? (
            <Settings
              setShowInfoDrawer={setShowInfoDrawer}
              t={t}
              fieldId={fieldId}
              selectedRecommendationItemOnTimeline={selectedRecommendationItemOnTimeline}
            />
          ) : currentStep === CMC_ROUTES.GOALS ? (
            <Goals setShowInfoDrawer={setShowInfoDrawer} maxSelectableItems={MAX_SELECTABLE_GOALS} t={t} />
          ) : currentStep === CMC_ROUTES.COMPOSITION ? (
            <MixComposition />
          ) : currentStep === CMC_ROUTES.OVERVIEW ? (
            <MixOverview />
          ) : currentStep === CMC_ROUTES.COM_MIXES ? (
            <CommercialCropMixesContent isDrawer={openInDrawer} />
          ) : null}
        </Flex>
      )}
      <ExtraInfo setVisible={setShowInfoDrawer} type="why-side-crops-matter" visible={showInfoDrawer} />
      <CropMixSelectionModal basePath={basePath} showModal={showCropMixSelectionModal} />
    </>
  );

  const footer = (
    <CropMixComposerFooter
      step={currentStep}
      onApplyToFieldClicked={handleApplyToField}
      onNextClicked={handleNextButton}
      onBackClicked={handleBackButton}
      fieldId={fieldId}
    />
  );

  const navigationCrumbs = useMemo(
    () => ({
      crumbs: [{ name: t("global:header.home"), link: "/home" }, { name: t("pageTitle") }],
      additionalData: (
        <CropMixComposerHeader
          basePath={basePath}
          activeCropMixId={activeCropMixId}
          isCommercialCropMix={currentStep === CMC_ROUTES.COM_MIXES}
          isPageLoading={isPageLoading}
        />
      ),
    }),
    [t, basePath, activeCropMixId, currentStep, isPageLoading]
  );

  // We need to set the navigation crumbs here because otherwise the mix selector dropdown is not updated correctly in the header
  useEffect(() => {
    if (!openInDrawer) setNavigationCrumbs(navigationCrumbs);
  }, [navigationCrumbs, openInDrawer, setNavigationCrumbs, currentStep]);

  const shouldShowFooter = currentStep && currentStep !== CMC_ROUTES.COM_MIXES && activeCropMixId;

  const overlayClassName = currentStep !== CMC_ROUTES.COM_MIXES ? `crop-mix-composer-overlay step-${currentStep}` : "";
  if (!openInDrawer) {
    return (
      <MainPage fullWidth pageName="crop-mix-composer-page" share={{ title: t("pageTitle") }} showMobileAlert>
        <div className="crop-mix-composer-page">
          <Flex vertical className={overlayClassName}>
            <div className="crop-mix-composer-page-content">{content()}</div>
            {shouldShowFooter ? <div className="crop-mix-composer-page-footer">{footer}</div> : null}
          </Flex>
        </div>
      </MainPage>
    );
  }

  return (
    <Drawer
      className={overlayClassName}
      dataTestId="crop-mix-composer-overlay"
      visible={isVisible}
      placement="bottom"
      height="calc(100vh - 1rem)"
      fullWidth
      onClose={handleClose}
      push={false}
      hideCloseIcon={currentStep === CMC_ROUTES.COM_MIXES}
      title={
        <CropMixComposerHeader basePath={basePath} activeCropMixId={activeCropMixId} isDrawer isCommercialCropMix={currentStep === CMC_ROUTES.COM_MIXES} />
      }
      footer={shouldShowFooter ? footer : null}
    >
      {content()}
    </Drawer>
  );
};
